import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicBySlug, getRelatedComics } from '@/mocks/comics';

function formatViews(views: number): string {
  if (views >= 10000) return (views / 10000).toFixed(1) + '万';
  return views.toString();
}

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab('info');
  }, [slug]);

  const comic = slug ? getComicBySlug(slug) : null;

  if (!comic) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-800 mb-2">作品未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该韩漫作品不存在或已被移除</p>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedComics(comic.slug, comic.category, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫屋`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.date,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫屋',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '韩漫屋首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: typeof window !== 'undefined' ? window.location.origin + '/category' : '' },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEO
        title={`${comic.title}_${comic.author}_在线观看下拉式韩漫-韩漫屋`}
        description={`韩漫屋提供${comic.title}免费在线阅读。${comic.description.substring(0, 100)}。下拉式观看模式，高清画质，每日更新韩国漫画。`}
        keywords={`${comic.title},${comic.author},韩国漫画,免费阅读,下拉式韩漫,韩漫屋,在线观看,${comic.tags.join(',')}`}
        canonicalUrl={`/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <article className="pt-20 pb-12 md:pb-16 bg-background-50" itemScope itemType="https://schema.org/Article">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览', to: '/category' },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mt-4">
            <div className="flex-shrink-0">
              <div className="w-full sm:w-[240px] md:w-[280px] aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - 韩漫屋`}
                  itemProp="image"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }} itemProp="headline">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-1" itemProp="author">
                作者：{comic.author}
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-foreground-100 text-foreground-700'
                }`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-star-fill text-yellow-500"></i>
                  <strong className="text-foreground-800">{comic.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line"></i>
                  {formatViews(comic.views)} 次阅读
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-book-open-line"></i>
                  {comic.chapters.length} 话
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-calendar-line"></i>
                  更新于 {comic.updateDate}
                </span>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap">{comic.category}</span>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.1)]">
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line text-lg"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-10">
            <div className="flex items-center border-b border-background-200">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-all cursor-pointer border-b-2 -mb-px ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品简介
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-all cursor-pointer border-b-2 -mb-px ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({comic.chapters.length})
              </button>
            </div>

            {activeTab === 'info' && (
              <div className="py-6" itemProp="description">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {comic.description}
                </p>
              </div>
            )}

            {activeTab === 'chapters' && (
              <div className="py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {comic.chapters.map((ch) => (
                    <div
                      key={ch.num}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                        {ch.title}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {related.length > 0 && (
            <section className="mt-12 md:mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {related.map((r) => (
                  <ComicCard key={r.id} {...r} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}